import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { useTimelineContext } from '../context/TimelineContext';
import { generateTimelineTicks } from '../utils/timelineTicks';
import { calculateEventOffsets as calculateTimelineEventLayout, TIMELINE_EVENT_LAYOUT } from '../utils/timelineEventLayout';
import './Timeline.css';

const SHOW_DEBUG_EVENTS = process.env.REACT_APP_SHOW_DEBUG_EVENTS === 'true';

const getEventImageUrl = (event) => {
  return event?.imageUrl || event?.imageURL || event?.image || '';
};

const {
  EVENT_WIDTH,
  RANGE_BAND_DISTANCE,
  RANGE_SPAN_HEIGHT,
  RANGE_LABEL_TOP,
  BASE_DISTANCE,
  MIN_RANGE_LABEL_WIDTH,
  RANGE_LABEL_VIEWPORT_PADDING
} = TIMELINE_EVENT_LAYOUT;

const Timeline = ({ onEventClick, onEventEdit, onEventDelete }) => {
  const timelineRef = useRef(null);
  const [scale, setScale] = useState(1 / (1000 * 60 * 60 * 24)); // Start at 1 pixel per day
  const [offset, setOffset] = useState(0);
  const { events, filteredEvents, setEditingEvent, deleteEvent, currentTimeline } = useTimelineContext();
  const eventsContainerRef = useRef(null);
  const hasUserNavigatedRef = useRef(false);
  const initializedTimelineIdRef = useRef(null);
  const hasInitialCenteredRef = useRef(false);
  const previousEventIdsRef = useRef(new Set());

  // Track mouse position and timing
  const mouseStateRef = useRef({
    isDragging: false,
    startX: 0,
    startOffset: 0,
    lastClickTime: 0
  });

  // Add state for context menu
  const [contextMenu, setContextMenu] = useState(null); // { kind, x, y, time?, event?, isConfirmingDelete? }
  const [highlightedOverflowEventId, setHighlightedOverflowEventId] = useState(null);

  // Add state for mouse press
  const [isPressed, setIsPressed] = useState(false);

  // Add window size state
  const [, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  const [showDebugInfo, setShowDebugInfo] = useState(false);

  const debugInfo = useMemo(() => {
    if (!SHOW_DEBUG_EVENTS) return null;
    if (!events) return "No events";
    return events.map(event => ({
      id: event.id,
      title: event.title,
      date: new Date(event.date).toLocaleString(),
      position: event.position
    }));
  }, [events]);

  const handleEventClick = useCallback((event) => {
    onEventClick?.(event);
  }, [onEventClick]);

  const handleEventKeyDown = useCallback((keyboardEvent, event) => {
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;

    keyboardEvent.preventDefault();
    handleEventClick(event);
  }, [handleEventClick]);

  const handleEventContextMenu = useCallback((mouseEvent, event) => {
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    setContextMenu({
      kind: 'event',
      x: mouseEvent.clientX,
      y: mouseEvent.clientY,
      event,
      isConfirmingDelete: false
    });
  }, []);

  const handleEditContextEvent = useCallback(() => {
    const event = contextMenu?.event;
    if (!event) return;

    setContextMenu(null);
    if (onEventEdit) {
      onEventEdit(event);
      return;
    }

    setEditingEvent(event);
    onEventClick?.();
  }, [contextMenu, onEventClick, onEventEdit, setEditingEvent]);

  const handleRequestDeleteContextEvent = useCallback(() => {
    setContextMenu(prev => prev?.kind === 'event'
      ? { ...prev, isConfirmingDelete: true }
      : prev
    );
  }, []);

  const handleCancelDeleteContextEvent = useCallback(() => {
    setContextMenu(prev => prev?.kind === 'event'
      ? { ...prev, isConfirmingDelete: false }
      : prev
    );
  }, []);

  const handleConfirmDeleteContextEvent = useCallback(() => {
    const event = contextMenu?.event;
    if (!event) return;

    deleteEvent(event.id);
    setContextMenu(null);
    onEventDelete?.(event);
  }, [contextMenu, deleteEvent, onEventDelete]);

  const formatEventMenuDate = useCallback((event) => {
    const date = event?.date instanceof Date ? event.date : new Date(event?.date);
    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      era: 'short'
    });
  }, []);

  const ensureDate = useCallback((dateInput) => {
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput === 'string' || typeof dateInput === 'number') {
      const date = new Date(dateInput);
      if (!isNaN(date)) return date;
    }
    console.error('Invalid date input:', dateInput);
    return null; // Return null to indicate invalid date
  }, []);

  const getViewportWidth = useCallback(() => {
    return timelineRef.current?.clientWidth || window.innerWidth || 1000;
  }, []);

  const getViewportHeight = useCallback(() => {
    return eventsContainerRef.current?.clientHeight || window.innerHeight || 700;
  }, []);

  const getViewportVerticalSpace = useCallback(() => {
    const container = eventsContainerRef.current;
    const fallbackHeight = getViewportHeight();
    if (!container) {
      return {
        top: fallbackHeight / 2,
        bottom: fallbackHeight / 2
      };
    }

    const rect = container.getBoundingClientRect();
    if (!rect.height) {
      return {
        top: fallbackHeight / 2,
        bottom: fallbackHeight / 2
      };
    }

    const axisViewportY = rect.top + (rect.height / 2);
    const visibleTop = Math.max(rect.top, 0);
    const visibleBottom = Math.min(rect.bottom, window.innerHeight || rect.bottom);
    if (visibleBottom <= visibleTop) {
      return {
        top: fallbackHeight / 2,
        bottom: fallbackHeight / 2
      };
    }

    return {
      top: Math.max(0, axisViewportY - visibleTop),
      bottom: Math.max(0, visibleBottom - axisViewportY)
    };
  }, [getViewportHeight]);

  const getViewportStartTime = useCallback(() => {
    return Date.now() - (offset / scale);
  }, [offset, scale]);

  const timeToPosition = useCallback((time) => {
    return (time - getViewportStartTime()) * scale;
  }, [getViewportStartTime, scale]);

  const positionToTime = useCallback((x) => {
    return getViewportStartTime() + (x / scale);
  }, [getViewportStartTime, scale]);

  const centerTimeInViewport = useCallback((time, targetX = getViewportWidth() / 2) => {
    setOffset(targetX - ((time - Date.now()) * scale));
  }, [getViewportWidth, scale]);

  const getTimelineSideForClientY = useCallback((clientY) => {
    const timelineRect = timelineRef.current?.getBoundingClientRect();
    if (!timelineRect) return 'top';

    return clientY > timelineRect.top + (timelineRect.height / 2) ? 'bottom' : 'top';
  }, []);

  const openEventDraft = useCallback((time, type = 'point', position = 'top') => {
    const draftDate = time instanceof Date ? time : new Date(time);
    setEditingEvent({
      id: null,
      title: '',
      description: '',
      date: draftDate,
      endDate: type === 'range' ? new Date(draftDate.getTime()) : null,
      type,
      position: type === 'range' ? 'bottom' : position
    });
    setContextMenu(null);
    onEventClick?.();
  }, [setEditingEvent, onEventClick]);

  const openCreateMenu = useCallback(({ x, y, time, position = 'top' }) => {
    setContextMenu({
      kind: 'create',
      x,
      y,
      time: time instanceof Date ? time : new Date(time),
      position
    });
  }, []);

  const handleAddEventAtViewportCenter = useCallback(() => {
    openEventDraft(positionToTime(getViewportWidth() / 2), 'point', 'top');
  }, [getViewportWidth, openEventDraft, positionToTime]);

  const handleCreatePointEvent = useCallback(() => {
    if (!contextMenu?.time) return;
    openEventDraft(contextMenu.time, 'point', contextMenu.position || 'top');
  }, [contextMenu, openEventDraft]);

  const handleCreateRangeEvent = useCallback(() => {
    if (!contextMenu?.time) return;
    openEventDraft(contextMenu.time, 'range');
  }, [contextMenu, openEventDraft]);

  const handleOverflowGroupClick = useCallback((mouseEvent, overflowGroup) => {
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    const targetRect = mouseEvent.currentTarget.getBoundingClientRect();
    setContextMenu({
      kind: 'overflow',
      x: targetRect.left + (targetRect.width / 2),
      y: targetRect.top - 8,
      events: overflowGroup.events
    });
  }, []);

  const handleOverflowGroupKeyDown = useCallback((keyboardEvent, overflowGroup) => {
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;

    handleOverflowGroupClick(keyboardEvent, overflowGroup);
  }, [handleOverflowGroupClick]);

  const handleOpenOverflowEvent = useCallback((event) => {
    setContextMenu(null);
    handleEventClick(event);
  }, [handleEventClick]);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.event-content, .context-menu, button, input, textarea, select')) {
      return;
    }

    e.preventDefault();
    setIsPressed(true);
    const mouseState = mouseStateRef.current;
    
    mouseState.startX = e.clientX;
    mouseState.startOffset = offset;
    
    mouseState.longPressTimer = setTimeout(() => {
      if (mouseState.startX === e.clientX) {
        const rect = timelineRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickTime = positionToTime(clickX);
        
        openCreateMenu({
          x: e.clientX,
          y: e.clientY - 10,
          time: clickTime,
          position: getTimelineSideForClientY(e.clientY)
        });
      }
    }, 500);
  }, [offset, getTimelineSideForClientY, openCreateMenu, positionToTime]);

  const handleMouseMove = useCallback((e) => {
    if (!isPressed) return;
    
    const mouseState = mouseStateRef.current;
    const deltaX = e.clientX - mouseState.startX;

    if (Math.abs(deltaX) > 5) {
      if (mouseState.longPressTimer) {
        clearTimeout(mouseState.longPressTimer);
        mouseState.longPressTimer = null;
      }
      
      setOffset(mouseState.startOffset + deltaX);
      hasUserNavigatedRef.current = true;
      mouseState.isDragging = true;
    }
  }, [isPressed]);

  const handleMouseUp = useCallback(() => {
    setIsPressed(false);
    const mouseState = mouseStateRef.current;
    
    if (mouseState.longPressTimer) {
      clearTimeout(mouseState.longPressTimer);
      mouseState.longPressTimer = null;
    }
    
    mouseState.isDragging = false;
    mouseState.startX = 0;
    mouseState.startOffset = 0;
  }, []);

  const handleClickOutside = useCallback((e) => {
    const contextMenuElement = document.querySelector('.context-menu');
    
    if (contextMenuElement && !contextMenuElement.contains(e.target)) {
      setContextMenu(null);
    }
  }, []);

  const handleWheel = useCallback((e) => {
    if (
      e.target.closest('.context-menu, input, textarea, select') ||
      (e.target.closest('button') && !e.target.closest('.timeline-overflow-button'))
    ) {
      return;
    }

    e.preventDefault();

    // Get mouse position relative to timeline
    const rect = timelineRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const timeAtMouse = positionToTime(mouseX);

    let zoomFactor;
    if (e.ctrlKey || e.metaKey) {
      // Pinch-to-zoom - make it more responsive
      zoomFactor = 1 - (e.deltaY * 0.005); // Increased from 0.001 to 0.005
    } else {
      // Regular scroll wheel zoom
      zoomFactor = Math.pow(1.001, -e.deltaY); // Smooth exponential zoom
    }

    const newScale = scale * zoomFactor;
    
    // Limit the scale to reasonable bounds
    const minScale = 1 / (1000 * 60 * 60 * 24 * 365 * 10); // 1 pixel per decade
    const maxScale = 1 / (1000 * 5); // 1 pixel per 5 seconds
    
    if (newScale >= minScale && newScale <= maxScale) {
      // Calculate new offset to keep the time under mouse cursor stable
      const newOffset = mouseX - ((timeAtMouse - Date.now()) * newScale);
      setScale(newScale);
      setOffset(newOffset);
      hasUserNavigatedRef.current = true;
    }
  }, [scale, positionToTime]);

  const calculateVisibleTimeRange = useCallback(() => {
    const startTime = getViewportStartTime();
    const viewportWidth = getViewportWidth();
    const endTime = startTime + (viewportWidth / scale);

    return { startTime, endTime };
  }, [scale, getViewportStartTime, getViewportWidth]);


  useEffect(() => {
    const currentTimelineId = currentTimeline?.id || null;
    if (initializedTimelineIdRef.current !== currentTimelineId) {
      initializedTimelineIdRef.current = currentTimelineId;
      previousEventIdsRef.current = new Set();
      hasUserNavigatedRef.current = false;
      hasInitialCenteredRef.current = false;
    }

    const validEvents = events
      .map(event => ({
        event,
        time: ensureDate(event.date)?.getTime()
      }))
      .filter(({ time }) => Number.isFinite(time));
    const currentEventIds = new Set(events.map(event => event.id));
    const previousEventIds = previousEventIdsRef.current;
    const addedEvents = validEvents.filter(({ event }) => !previousEventIds.has(event.id));

    if (!hasInitialCenteredRef.current) {
      const earliestEvent = validEvents.reduce((earliest, current) => {
        if (!earliest || current.time < earliest.time) return current;
        return earliest;
      }, null);

      centerTimeInViewport(earliestEvent ? earliestEvent.time : Date.now());
      hasInitialCenteredRef.current = true;
    } else if (addedEvents.length > 0) {
      const newestAddedEvent = addedEvents.reduce((newest, current) => {
        if (!newest) return current;
        if (Number(current.event.id) > Number(newest.event.id)) return current;
        return newest;
      }, null);

      if (newestAddedEvent) {
        centerTimeInViewport(newestAddedEvent.time);
      }
    } else if (!hasUserNavigatedRef.current && validEvents.length > 0) {
      const earliestEvent = validEvents.reduce((earliest, current) => {
        if (!earliest || current.time < earliest.time) return current;
        return earliest;
      }, null);

      if (earliestEvent) {
        centerTimeInViewport(earliestEvent.time);
      }
    }

    previousEventIdsRef.current = currentEventIds;
  }, [events, currentTimeline?.id, ensureDate, centerTimeInViewport]);

  const renderedEvents = useMemo(() => {
    if (!filteredEvents?.length) return null;
    
    const { startTime } = calculateVisibleTimeRange();
    const viewportWidth = getViewportWidth();
    const viewportHeight = getViewportHeight();
    const verticalSpace = getViewportVerticalSpace();
    const eventLayout = calculateTimelineEventLayout({
      events: filteredEvents,
      currentScale: scale,
      currentStartTime: startTime,
      viewportWidth,
      viewportHeight,
      verticalSpace,
      ensureDate
    });
    const eventOffsets = eventLayout.offsets;
    
    const renderedEventNodes = filteredEvents.map(event => {
      const eventDate = ensureDate(event.date)?.getTime();
      if (!Number.isFinite(eventDate)) return null;
      
      const verticalOffset = eventOffsets.get(event.id) || { verticalOffset: 0, isOverlapping: false };
      if (verticalOffset.isOverflowed) return null;

      const imageUrl = getEventImageUrl(event);
      const useCompactEvent = Boolean(verticalOffset.useCompactEvent);
      const useCompactImage = Boolean(imageUrl && verticalOffset.useCompactImage);
      
      // Calculate position relative to the viewport start time
      const positionPx = timeToPosition(eventDate);
      const isRange = event.type === 'range' && event.endDate;
      const eventPosition = event.position || 'top';
      const isBottom = isRange || eventPosition === 'bottom';
      const baseDistance = BASE_DISTANCE;
      
      // Calculate end position for range events
      const eventEndDate = event.type === 'range' && event.endDate
        ? ensureDate(event.endDate)?.getTime()
        : null;
      const endPositionPx = Number.isFinite(eventEndDate)
        ? timeToPosition(eventEndDate)
        : positionPx;
      const rangeLeftPx = verticalOffset.rangeLeft ?? Math.min(positionPx, endPositionPx);
      const rangeWidthPx = Math.abs(endPositionPx - positionPx);
      const renderedRangeWidthPx = verticalOffset.rangeWidth ?? rangeWidthPx;
      const rangeRightPx = rangeLeftPx + renderedRangeWidthPx;
      const rangeTopPx = verticalOffset.rangeOffset || 0;
      const visibleRangeStartPx = Math.max(0, rangeLeftPx);
      const visibleRangeEndPx = Math.min(viewportWidth, rangeRightPx);
      const hasVisibleRange = renderedRangeWidthPx === 0
        ? rangeLeftPx >= 0 && rangeLeftPx <= viewportWidth
        : visibleRangeEndPx > visibleRangeStartPx;
      const rangeCenterPx = rangeLeftPx + (renderedRangeWidthPx / 2);
      const rangeLabelWidthPx = isRange
        ? Math.min(260, Math.max(MIN_RANGE_LABEL_WIDTH, String(event.title || '').length * 7.5 + 32))
        : EVENT_WIDTH;
      const minLabelCenterPx = Math.min(
        (rangeLabelWidthPx / 2) + RANGE_LABEL_VIEWPORT_PADDING,
        viewportWidth / 2
      );
      const maxLabelCenterPx = Math.max(
        viewportWidth - (rangeLabelWidthPx / 2) - RANGE_LABEL_VIEWPORT_PADDING,
        viewportWidth / 2
      );
      const rangeLabelCenterPx = Math.min(
        Math.max(rangeCenterPx, minLabelCenterPx),
        maxLabelCenterPx
      );
      const rangeLabelAbsoluteLeftPx = rangeLabelCenterPx - (rangeLabelWidthPx / 2);
      const rangeVisualLeftPx = isRange
        ? Math.min(rangeLeftPx, rangeLabelAbsoluteLeftPx)
        : positionPx;
      const rangeVisualRightPx = isRange
        ? Math.max(rangeRightPx, rangeLabelAbsoluteLeftPx + rangeLabelWidthPx)
        : positionPx + EVENT_WIDTH;
      const rangeVisualWidthPx = rangeVisualRightPx - rangeVisualLeftPx;
      const rangeLabelLeftPx = rangeLabelAbsoluteLeftPx - rangeVisualLeftPx;
      const rangeLineLeftPx = rangeLeftPx - rangeVisualLeftPx;
      const rangeLineRightPx = rangeLineLeftPx + renderedRangeWidthPx;
      const rangeLabelRightPx = rangeLabelLeftPx + rangeLabelWidthPx;
      const bracketCurveInsetPx = isRange
        ? Math.min(14, Math.max(5, renderedRangeWidthPx / 4))
        : 0;
      const useStraightBracketEnds = isRange && (
        renderedRangeWidthPx <= rangeLabelWidthPx ||
        (rangeLineLeftPx >= rangeLabelLeftPx && rangeLineRightPx <= rangeLabelRightPx)
      );
      const rangeBracketPath = isRange
        ? useStraightBracketEnds
          ? renderedRangeWidthPx < 1
            ? `M ${rangeLineLeftPx} 0 L ${rangeLineLeftPx} ${RANGE_LABEL_TOP}`
            : [
                `M ${rangeLineLeftPx} 0`,
                `L ${rangeLineLeftPx} ${RANGE_LABEL_TOP}`,
                `M ${rangeLineRightPx} 0`,
                `L ${rangeLineRightPx} ${RANGE_LABEL_TOP}`
              ].join(' ')
          : [
              `M ${rangeLineLeftPx} 0`,
              `C ${rangeLineLeftPx} ${RANGE_LABEL_TOP - 4}`,
              `${rangeLineLeftPx + 5} ${RANGE_LABEL_TOP}`,
              `${rangeLineLeftPx + bracketCurveInsetPx} ${RANGE_LABEL_TOP}`,
              `L ${rangeLineRightPx - bracketCurveInsetPx} ${RANGE_LABEL_TOP}`,
              `C ${rangeLineRightPx - 5} ${RANGE_LABEL_TOP}`,
              `${rangeLineRightPx} ${RANGE_LABEL_TOP - 4}`,
              `${rangeLineRightPx} 0`
            ].join(' ')
        : '';
      const isRangeClippedStart = isRange && rangeLeftPx < 0;
      const isRangeClippedEnd = isRange && rangeRightPx > viewportWidth;
      const isRangeOutOfView = isRange && !hasVisibleRange;

      return (
        <div
          key={event.id}
          data-tutorial-event-id={event.id}
          className={`timeline-event ${isBottom ? 'bottom' : 'top'} ${event.type || 'point'} ${imageUrl ? 'has-image' : ''} ${useCompactEvent ? 'compact-event' : ''} ${useCompactImage ? 'compact-image' : ''} ${verticalOffset.isOverlapping ? 'overlapping' : ''}${isRangeClippedStart ? ' clipped-start' : ''}${isRangeClippedEnd ? ' clipped-end' : ''}${isRangeOutOfView ? ' out-of-view' : ''}`}
          style={{
            position: 'absolute',
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          <div 
            className="event-content"
            role="button"
            tabIndex={0}
            aria-label={`View details for ${event.title}`}
            onClick={() => handleEventClick(event)}
            onContextMenu={(e) => handleEventContextMenu(e, event)}
            onKeyDown={(e) => handleEventKeyDown(e, event)}
            style={{
              position: 'absolute',
              left: isRange ? `${rangeVisualLeftPx}px` : `${positionPx}px`,
              width: isRange ? `${rangeVisualWidthPx}px` : `${EVENT_WIDTH}px`,
              transform: isRange ? 'none' : 'translateX(-50%)',
              ...(isBottom 
                ? { 
                    top: '50%', 
                    marginTop: isRange
                      ? RANGE_BAND_DISTANCE + rangeTopPx
                      : baseDistance + (verticalOffset.verticalOffset || 0)
                  }
                : { 
                    bottom: '50%', 
                    marginBottom: baseDistance + (verticalOffset.verticalOffset || 0)
                  }
              ),
              pointerEvents: 'auto',
              ...(isRange
                ? {
                    '--range-span-height': `${RANGE_SPAN_HEIGHT}px`,
                    '--range-label-left': `${rangeLabelLeftPx}px`,
                    '--range-label-width': `${rangeLabelWidthPx}px`,
                    '--range-line-left': `${rangeLineLeftPx}px`,
                    '--range-line-width': `${renderedRangeWidthPx}px`,
                    '--range-label-top': `${RANGE_LABEL_TOP}px`
                  }
                : {})
            }}
          >
            {isRange && (
              <svg
                className="range-bracket"
                aria-hidden="true"
                focusable="false"
                viewBox={`0 0 ${rangeVisualWidthPx} ${RANGE_LABEL_TOP + 4}`}
                preserveAspectRatio="none"
              >
                <path className="range-bracket-path" d={rangeBracketPath} />
              </svg>
            )}
            {isRange && imageUrl && (
              <figure className="range-image-thumb">
                <img src={imageUrl} alt="" loading="lazy" />
              </figure>
            )}
            {!isRange && imageUrl && !useCompactImage && (
              <figure className="event-image-thumb">
                <img src={imageUrl} alt="" loading="lazy" />
              </figure>
            )}
            {!isRange && imageUrl && useCompactImage && (
              <figure className="event-image-inline-thumb">
                <img src={imageUrl} alt="" loading="lazy" />
              </figure>
            )}
            <h3>{event.title}</h3>
            {event.description && (
              <p className="event-description">{event.description}</p>
            )}
          </div>
          {!isRange && (
            <div 
              className="event-connector"
              style={{
                position: 'absolute',
                left: `${positionPx}px`,
                width: '2px',
                transform: 'translateX(-50%)',
                ...(isBottom 
                  ? { 
                      top: '50%',
                      height: `${baseDistance + (verticalOffset.verticalOffset || 0)}px`
                    }
                  : { 
                      bottom: '50%',
                      height: `${baseDistance + (verticalOffset.verticalOffset || 0)}px`
                    }
                ),
                pointerEvents: 'none'
              }}
            />
          )}
        </div>
      );
    });

    const renderedOverflowGroups = eventLayout.overflowGroups.map(overflowGroup => {
      const isBottom = overflowGroup.side === 'bottom';
      const isRangeOverflow = overflowGroup.laneType === 'range';
      const hiddenCount = overflowGroup.events.length;
      const hiddenEventLabel = `${hiddenCount} hidden ${hiddenCount === 1 ? 'event' : 'events'} at this zoom level`;
      const axisY = viewportHeight / 2;
      const markerY = isRangeOverflow
        ? axisY + RANGE_BAND_DISTANCE + (overflowGroup.verticalOffset || 0)
        : isBottom
        ? axisY + BASE_DISTANCE + (overflowGroup.verticalOffset || 0)
        : axisY - BASE_DISTANCE - (overflowGroup.verticalOffset || 0);

      return (
        <div
          key={`overflow-${overflowGroup.id}`}
          className={`timeline-overflow-group ${isBottom ? 'bottom' : 'top'} ${isRangeOverflow ? 'range' : 'point'}`}
          style={{
            position: 'absolute',
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          <button
            type="button"
            className="timeline-overflow-button"
            aria-label={hiddenEventLabel}
            onClick={(e) => handleOverflowGroupClick(e, overflowGroup)}
            onKeyDown={(e) => handleOverflowGroupKeyDown(e, overflowGroup)}
            style={{
              position: 'absolute',
              left: `${overflowGroup.positionPx}px`,
              transform: 'translateX(-50%)',
              ...(isBottom
                ? {
                    top: '50%',
                    marginTop: (isRangeOverflow ? RANGE_BAND_DISTANCE : BASE_DISTANCE) +
                      (overflowGroup.verticalOffset || 0)
                  }
                : {
                    bottom: '50%',
                    marginBottom: BASE_DISTANCE + (overflowGroup.verticalOffset || 0)
                  }
              ),
              pointerEvents: 'auto'
            }}
          >
            +{hiddenCount} more
          </button>
          <svg
            className="timeline-overflow-lines"
            aria-hidden="true"
            focusable="false"
            width="100%"
            height="100%"
          >
            {overflowGroup.eventPositions.map(eventPosition => (
              <path
                key={`overflow-line-${overflowGroup.id}-${eventPosition.id}`}
                className={`timeline-overflow-line ${highlightedOverflowEventId === eventPosition.id ? 'active' : ''}`}
                d={`M ${overflowGroup.positionPx} ${markerY} L ${eventPosition.positionPx} ${axisY}`}
              />
            ))}
          </svg>
        </div>
      );
    });

    return (
      <>
        {renderedEventNodes}
        {renderedOverflowGroups}
      </>
    );
  }, [filteredEvents, scale, handleEventClick, handleEventContextMenu, handleEventKeyDown, handleOverflowGroupClick, handleOverflowGroupKeyDown, highlightedOverflowEventId, ensureDate, calculateVisibleTimeRange, timeToPosition, getViewportWidth, getViewportHeight, getViewportVerticalSpace]);

  const timeMarkers = useMemo(() => {
    const viewportWidth = getViewportWidth();
    const startTime = getViewportStartTime();
    const endTime = startTime + (viewportWidth / scale);

    const tickSet = generateTimelineTicks({
      startTime,
      endTime,
      viewportWidth,
      scale
    });

    const renderTick = (tick) => (
      <div
        key={tick.key}
        className={`timeline-marker ${tick.kind}${tick.boundary ? ' boundary' : ''}`}
        style={{ left: `${tick.positionPx}px` }}
      >
        <div className="marker-line" />
        {tick.label && (
          <div className={`marker-label ${tick.labelAlign}`}>{tick.label}</div>
        )}
      </div>
    );

    const renderContextLabel = (tick) => (
      <div
        key={tick.key}
        className={`timeline-context-label ${tick.labelAlign}`}
        style={{ left: `${tick.positionPx}px` }}
      >
        {tick.label}
      </div>
    );

    const renderSpanMarker = (marker) => (
      <div
        key={marker.key}
        className={`timeline-span-marker ${marker.unit}`}
        style={{
          left: `${marker.visibleStartPx}px`,
          width: `${marker.visibleWidthPx}px`
        }}
      >
        <div className="timeline-span-bracket" />
        <div
          className="timeline-span-label"
          style={{ left: `${marker.labelPx - marker.visibleStartPx}px` }}
        >
          {marker.label}
        </div>
      </div>
    );

    return (
      <>
        {tickSet.spanMarkers.map(renderSpanMarker)}
        {tickSet.minorTicks.map(renderTick)}
        {tickSet.majorTicks.map(renderTick)}
        {tickSet.spanMarkers.length === 0 && tickSet.contextLabels.map(renderContextLabel)}
      </>
    );
  }, [scale, getViewportWidth, getViewportStartTime]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (!contextMenu) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setContextMenu(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [contextMenu]);

  useEffect(() => {
    const timeline = eventsContainerRef.current;
    if (!timeline) return;
    
    timeline.addEventListener('wheel', handleWheel, { passive: false });
    return () => timeline.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleContextMenu = useCallback((e) => {
    if (e.target.closest('.event-content, .context-menu, button, input, textarea, select')) {
      return;
    }

    e.preventDefault();
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickTime = positionToTime(clickX);
    
    openCreateMenu({
      x: e.clientX,
      y: e.clientY,
      time: clickTime,
      position: getTimelineSideForClientY(e.clientY)
    });
  }, [positionToTime, getTimelineSideForClientY, openCreateMenu]);

  const hasEvents = events.length > 0;
  const hasVisibleEvents = filteredEvents.length > 0;
  const showEmptyState = !hasVisibleEvents;
  const emptyStateTitle = hasEvents ? 'No Events Match This Filter' : 'No Events Yet';
  const emptyStateText = hasEvents
    ? 'Change the tag filter, or use Create Event to add a new event to this timeline.'
    : 'Use Create Event to add an event at the date currently centered in the timeline.';
  const contextMenuLeft = contextMenu
    ? Math.min(Math.max(contextMenu.x, 12), (window.innerWidth || contextMenu.x) - 12)
    : 0;
  const contextMenuTop = contextMenu
    ? Math.min(Math.max(contextMenu.y, 12), (window.innerHeight || contextMenu.y) - 12)
    : 0;
  const contextMenuTransform = contextMenu?.y < 220
    ? 'translate(-50%, 12px)'
    : 'translate(-50%, -100%)';

  return (
    <div 
      className="timeline-wrapper"
      ref={eventsContainerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onContextMenu={handleContextMenu}
      style={{ 
        cursor: mouseStateRef.current.isDragging ? 'grabbing' : 'grab'
      }}
    >
      {SHOW_DEBUG_EVENTS && (
        <>
          <button
            onClick={() => setShowDebugInfo(!showDebugInfo)}
            className="debug-toggle"
          >
            Debug Events
          </button>

          {showDebugInfo && (
            <div className="debug-panel">
              <h3>Events ({events.length})</h3>
              <pre>
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}

      <div 
        className="timeline-events"
      >
        <button
          type="button"
          className="timeline-quick-add-button"
          onClick={handleAddEventAtViewportCenter}
          title="Create Event"
        >
          <span aria-hidden="true">+</span>
          <span>Create Event</span>
        </button>
        {showEmptyState && (
          <div className="timeline-empty-state">
            <div className="timeline-empty-kicker">{currentTimeline?.name || 'Timeline'}</div>
            <h2>{emptyStateTitle}</h2>
            <p>{emptyStateText}</p>
          </div>
        )}
        {renderedEvents}
      </div>
      <div 
        className="timeline-main" 
        ref={timelineRef}
      >
        <div className="timeline-line"></div>
        <div className="timeline-markers">
          {timeMarkers}
        </div>
      </div>
      {contextMenu && (
        <div 
          className={`context-menu ${contextMenu.kind === 'event' ? 'event-context-menu' : ''}`}
          role="menu"
          aria-label={contextMenu.kind === 'event'
            ? 'Event actions'
            : contextMenu.kind === 'overflow'
              ? 'Hidden events'
              : 'Timeline actions'
          }
          style={{
            position: 'fixed',
            top: contextMenuTop,
            left: contextMenuLeft,
            transform: contextMenuTransform,
            zIndex: 1000
          }}
        >
          {contextMenu.kind === 'event' ? (
            contextMenu.isConfirmingDelete ? (
              <div className="context-menu-confirmation">
                <p>Delete this event?</p>
                <div className="context-menu-actions">
                  <button
                    type="button"
                    className="context-menu-delete"
                    onClick={handleConfirmDeleteContextEvent}
                  >
                    Delete
                  </button>
                  <button type="button" onClick={handleCancelDeleteContextEvent}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button type="button" role="menuitem" onClick={handleEditContextEvent}>
                  Edit Event
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="context-menu-delete"
                  onClick={handleRequestDeleteContextEvent}
                >
                  Delete Event
                </button>
              </>
            )
          ) : contextMenu.kind === 'overflow' ? (
            <>
              {contextMenu.events.map(event => (
                <button
                  key={event.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleOpenOverflowEvent(event)}
                  onMouseEnter={() => setHighlightedOverflowEventId(event.id)}
                  onFocus={() => setHighlightedOverflowEventId(event.id)}
                  onMouseLeave={() => setHighlightedOverflowEventId(null)}
                  onBlur={() => setHighlightedOverflowEventId(null)}
                >
                  <span>{event.title}</span>
                  <span className="context-menu-item-date">{formatEventMenuDate(event)}</span>
                </button>
              ))}
            </>
          ) : (
            <>
              <button
                type="button"
                role="menuitem"
                data-tutorial-id="create-point-event"
                onClick={handleCreatePointEvent}
              >
                Point Event
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={handleCreateRangeEvent}
              >
                Range Event
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Timeline;
